import { useMemo } from "react";
import { keyBy } from "lodash";
import { DrawerBody, DrawerFooter } from "@chakra-ui/react";
import { getFragmentData, gql } from "__generated__/gql";
import { assertPresence } from "utils/assertions";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { makeClearNestedListFieldCache } from "utils/apollo/cacheHelpers";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import { CancelButton, SaveButton } from "components/Products/EditPanelContent";
import { SkuComponentType } from "__generated__/gql/graphql";
import { IsUpcPresentByType } from "./SkuComponents";
import {
  EditSkuFragment,
  SkuInputs,
  fragment,
  getSkuFormDefaultValues,
  resetSkuRelationsCache,
  skuComponentFieldsFragment,
  useGetMutationDataFromFormData,
} from "./utils";
import SkuPanelForm, {
  FORM_ID,
  processApolloError,
  SubmitContext,
} from "./Form";

const mutation = gql(/* GraphQL */ `
  mutation UpdateSku(
    $skuId: UUID!
    $patch: SkuPatch!
    $dimensions: [DimensionInput!]!
    $tiHis: [TiHiInput!]!
    $skuComponents: [SkuComponentInput!]!
  ) {
    ...UpdateSkuO2O
    updateSku(input: { id: $skuId, patch: $patch }) {
      sku {
        id
        name
        productLineId
        ...SelectedSkuDetailsFields
        ...ProductsTableSku
      }
    }
    ...UpdateSkuM2M
  }
`);

type Props = {
  isLoading?: boolean;
  onCancel?: () => void;
  onComplete?: () => void;
  sku: EditSkuFragment | null | undefined;
};

export default function SkuPanelEdit({
  isLoading: isLoadingData,
  onCancel,
  onComplete,
  sku: skuFragment,
}: Props) {
  const sku = getFragmentData(fragment, skuFragment);

  const [updateSku, { loading: isSaving }] = useBrandMutation(mutation, {
    update: resetSkuRelationsCache,
  });

  const skuComponentsByType = useMemo(() => {
    const skuComponents = getFragmentData(
      skuComponentFieldsFragment,
      sku,
    )?.skuComponentsList;
    if (!skuComponents) return {};
    return keyBy(skuComponents, "skuComponentType");
  }, [sku]);

  const disabledComponentTypes = useMemo(() => {
    const scts: SkuComponentType[] = [];
    if (!skuComponentsByType[SkuComponentType.INNER]) {
      scts.push(SkuComponentType.INNER);
    }
    return scts;
  }, [skuComponentsByType]);

  const defaultValues = useMemo(() => {
    if (!skuFragment) return;
    return getSkuFormDefaultValues(skuFragment);
  }, [skuFragment]);

  const getMutationDataFromFormData = useGetMutationDataFromFormData(sku);

  const [handleValidSubmit, { isLoading }] = useCreateValidSubmitHandler(
    async (formData: SkuInputs, context: SubmitContext) => {
      assertPresence(sku?.id);

      const { skuData, dimensions, tiHis, skuComponents } =
        await getMutationDataFromFormData(formData, context, sku.id);

      const oldProductLineId = sku?.productLineId;

      const { data: updateData } = await updateSku({
        variables: {
          patch: skuData,
          skuId: sku.id,
          dimensions,
          tiHis,
          skuComponents,
        },
        update: (cache, result) => {
          const updatedSku = result.data?.updateSku?.sku;
          assertPresence(updatedSku);
          if (
            oldProductLineId &&
            updatedSku.productLineId !== oldProductLineId
          ) {
            const clearNestedListFieldCache =
              makeClearNestedListFieldCache(cache);
            clearNestedListFieldCache("ProductLine", ["skus", "skusList"]);
          }
        },
      });
      const updatedSku = updateData?.updateSku?.sku;
      assertPresence(updatedSku);
      return updatedSku;
    },
    {
      getSuccessToast: (result) => ({
        title: `SKU updated successfully: ${result?.name}`,
      }),
      processApolloError,
      onUpdateComplete: onComplete,
    },
  );

  const idEditKey = `${sku?.id}-${sku?.updatedAt}`;

  const isUpcPresentByType = useMemo(
    () =>
      getFragmentData(
        skuComponentFieldsFragment,
        sku,
      )?.skuComponentsList?.reduce<IsUpcPresentByType>((acc, sc) => {
        acc[sc.skuComponentType] = !!sc.upc;
        return acc;
      }, {}),
    [sku],
  );

  return (
    <>
      <DrawerBody>
        <SkuPanelForm
          onValidSubmit={handleValidSubmit}
          {...{
            defaultValues,
            disabledComponentTypes,
            idEditKey,
            isUpcPresentByType,
          }}
          isInternalSkuIdPresent={!!sku?.internalSkuId}
          isLoading={isLoadingData}
        />
      </DrawerBody>

      <DrawerFooter gap={4}>
        {onCancel && <CancelButton onClick={onCancel} />}

        <SaveButton
          isDisabled={!sku?.id}
          isLoading={isLoading || isSaving}
          form={FORM_ID}
        />
      </DrawerFooter>
    </>
  );
}
